import { useEmulatorRows } from "@app/emulators";
import { useSession } from "@app/session";
import { availability, useFeature } from "@config/features";
import {
  useAccountLinkURL,
  useSnapshots,
  useTenantBootstrapUpdates,
  useTenantGroups,
  useTenantMembers,
  useTenants,
} from "@repo/components/ctrl/hooks";
import { useMemo } from "react";
import { useLocation } from "react-router";
import { buildNav, emuTabFrom } from "../nav";

/** Live inputs (role, route, counts) → the shared nav model. */
export function useNavModel() {
  const { pathname, search } = useLocation();
  const session = useSession();
  const emus = useEmulatorRows();
  const { data: snaps } = useSnapshots({ revalidateOnFocus: false });
  const admin = session.isAdmin ? session.tenantId : undefined;
  const { data: members } = useTenantMembers(admin);
  const { data: groups } = useTenantGroups(admin);
  const { data: updates } = useTenantBootstrapUpdates(admin);
  const { data: tenants } = useTenants({ isPaused: () => !session.isGlobalAdmin });
  const { data: link } = useAccountLinkURL();
  const toolDistribution = useFeature("toolDistribution");

  const emuId = pathname.match(/^\/emulators\/([^/]+)/)?.[1];
  const current = emus.rows.find((r) => r.id === emuId);
  const tenantId = pathname.match(/^\/platform\/tenants\/([^/]+)/)?.[1];

  return useMemo(() => {
    const failedSnaps = (snaps ?? []).filter((s) => {
      const st = s.labels?.["null.autos/job-status"];
      return st === "failed" || st === "canceled" || (s as { phantom?: boolean }).phantom;
    }).length;
    const unlinked = (members ?? []).filter(
      (m) => (m as { linked?: boolean }).linked === false,
    ).length;
    const infraUpdates = Object.values(
      (updates as { updates?: Record<string, boolean> } | undefined)?.updates ?? {},
    ).filter(Boolean).length;

    return {
      ...buildNav({
        role: session.role,
        pathname,
        search,
        workspaces: availability("workspaces"),
        toolDistribution,
        tenantName: tenants?.find((t) => t.id === tenantId)?.name,
        emulator: current
          ? {
              id: current.id,
              name: current.name,
              running: current.status === "Running",
              tab: current.mine ? "mine" : current.shared || !session.isAdmin ? "shared" : "all",
            }
          : undefined,
        counts: {
          emuMine: emus.mine.length,
          emuShared: emus.shared.length,
          emuAll: session.isAdmin ? emus.rows.length : undefined,
          running: emus.running.length,
          failedSnaps,
          members: members?.length,
          groups: groups?.length,
          unlinkedMembers: unlinked,
          infraUpdates,
          tenants: tenants?.length,
          gitlabLinked: link ? Boolean(link.vcs_linked) : undefined,
        },
      }),
      session,
      emus,
      emuTab: emuTabFrom(search, session.role),
    };
  }, [session, pathname, search, emus, snaps, members, groups, updates, tenants, link, current, tenantId, toolDistribution]);
}
